/**
 * Con que paga: la lista de medios de pago.
 *
 * Es el mismo editor que usan el ticket de venta, el cobro de cuenta corriente,
 * la entrega de una reparacion y la seña, para que todo cobre igual. Cada fila
 * (`FilaPago`) es una caja (que impone la moneda), un medio si la caja acepta
 * mas de uno, y un importe.
 *
 * No decide nada de plata: quien lo usa le pasa los pagos y recibe los pagos.
 * Lo unico que hace solo es proponer, en una fila nueva, lo que falta para
 * llegar a `objetivo`, convertido a la moneda de esa caja.
 */
import { useCallback, useEffect, useRef } from "react";
import { convertir, type Moneda } from "@tora/shared";

import { Button } from "@/components/ui/button";
import { FilaPago } from "@/components/cobro/FilaPago";
import {
  cuentaPorDefecto,
  mediosDe,
  proponerImporte,
  type CuentaFinanciera,
  type PagoCobro,
} from "@/components/cobro/logica";

export interface EditorPagosProps {
  pagos: PagoCobro[];
  onChange: (pagos: PagoCobro[]) => void;
  cuentas: CuentaFinanciera[];
  /** Lo que falta cobrar, en `monedaObjetivo`. Se usa para proponer importes. */
  objetivo: number;
  monedaObjetivo: Moneda;
  cotizacion: number;
  /** Con `true`, la lista puede quedar vacia (la entrega a cuenta corriente). */
  permitirVacio?: boolean;
  /** Una sola fila, sin "agregar otro": la seña. */
  unoSolo?: boolean;
  /** Texto del boton de agregar cuando todavia no hay ningun pago ("¿Cómo paga?" en el ticket). */
  etiquetaVacio?: string;
}

export function nuevoPago(
  cuentas: CuentaFinanciera[],
  pagosPrevios: PagoCobro[],
  objetivo: number,
  monedaObjetivo: Moneda,
  cotizacion: number,
): PagoCobro | null {
  const cuenta = cuentaPorDefecto(cuentas);
  if (!cuenta) return null;
  return {
    id: crypto.randomUUID(),
    cuenta_id: cuenta.id,
    medio: mediosDe(cuenta)[0]?.valor ?? "OTRO",
    importe: proponerImporte(objetivo, pagosPrevios, cuentas, monedaObjetivo, cuenta, cotizacion),
  };
}

export function EditorPagos({
  pagos,
  onChange,
  cuentas,
  objetivo,
  monedaObjetivo,
  cotizacion,
  permitirVacio = false,
  unoSolo = false,
  etiquetaVacio = "Agregar un medio de pago",
}: EditorPagosProps): JSX.Element {
  /**
   * Por renglon, el importe de antes de pasarlo a otra moneda. Si se vuelve a
   * la caja de la moneda original sin haber tocado el numero, vuelve el
   * original: convertir de ida y de vuelta redondeando a centavos corria el
   * importe unos pesos ($ 67.500 → US$ 42,45 → $ 67.495,50).
   */
  const ida = useRef(new Map<string, { moneda: Moneda; valor: number; convertido: number }>());

  /**
   * El importe de un renglon que pasa a una caja de otra moneda.
   *
   *   · Si era el que el sistema propuso (lo que faltaba cubrir), se vuelve a
   *     proponer para la caja nueva: sale exacto, sin arrastrar redondeos.
   *   · Si lo escribio una persona, se convierte con la cotizacion, y se
   *     recuerda el original por si vuelve a la moneda de antes.
   */
  function importeEnOtraMoneda(
    p: PagoCobro,
    antes: CuentaFinanciera,
    cuenta: CuentaFinanciera,
  ): number {
    const importe = p.importe ?? 0;
    const previo = ida.current.get(p.id);
    if (
      previo &&
      previo.moneda === cuenta.moneda &&
      Math.abs(previo.convertido - importe) < 0.005
    ) {
      ida.current.delete(p.id);
      return previo.valor;
    }
    const otros = pagos.filter((x) => x.id !== p.id);
    const propuesto = proponerImporte(objetivo, otros, cuentas, monedaObjetivo, antes, cotizacion);
    const nuevo =
      Math.abs(propuesto - importe) < 0.005
        ? proponerImporte(objetivo, otros, cuentas, monedaObjetivo, cuenta, cotizacion)
        : Math.round(convertir(importe, antes.moneda, cuenta.moneda, cotizacion) * 100) / 100;
    ida.current.set(p.id, { moneda: antes.moneda, valor: importe, convertido: nuevo });
    return nuevo;
  }

  const actualizar = useCallback(
    (id: string, cambios: Partial<PagoCobro>) => {
      if (cambios.importe !== undefined) ida.current.delete(id);
      onChange(
        pagos.map((p) => {
          if (p.id !== id) return p;
          const sig = { ...p, ...cambios };
          if (cambios.cuenta_id !== undefined) {
            const cuenta = cuentas.find((c) => c.id === cambios.cuenta_id);
            if (cuenta) {
              const validos = mediosDe(cuenta).map((m) => m.valor);
              if (!validos.includes(sig.medio)) sig.medio = validos[0] ?? "OTRO";
              // La caja impone la moneda: pasar de pesos a dolares con el mismo
              // numero convertia $ 97.005 en US$ 97.005 y asentaba un vuelto
              // absurdo.
              const antes = cuentas.find((c) => c.id === p.cuenta_id);
              if (
                antes &&
                antes.moneda !== cuenta.moneda &&
                sig.importe !== null &&
                cambios.importe === undefined
              ) {
                sig.importe = importeEnOtraMoneda(p, antes, cuenta);
              }
            }
          }
          return sig;
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cuentas, cotizacion, objetivo, monedaObjetivo, onChange, pagos],
  );

  /*
   * Cuando cambia lo que hay que cobrar o la cotizacion, los renglones que
   * siguen en el importe que el sistema propuso se vuelven a proponer. Lo que
   * escribio una persona no se toca.
   *
   *   · El total: abrir «¿Cómo paga?» y despues hacer un descuento o sumar un
   *     producto dejaba la fila en el total viejo, con un vuelto que no existe
   *     ($ 23.000 propuestos contra $ 20.700 a cobrar: «Vuelto $ 2.300»).
   *   · La cotizacion: el dolar pactado con un proveedor se carga despues de
   *     elegir la caja.
   */
  const anterior = useRef({ objetivo, cotizacion, monedaObjetivo });
  useEffect(() => {
    const viejo = anterior.current;
    anterior.current = { objetivo, cotizacion, monedaObjetivo };
    if (
      viejo.objetivo === objetivo &&
      viejo.cotizacion === cotizacion &&
      viejo.monedaObjetivo === monedaObjetivo
    ) {
      return;
    }
    if (!(viejo.cotizacion > 0) || !(cotizacion > 0)) return;
    let cambio = false;
    const siguientes: PagoCobro[] = [];
    pagos.forEach((p, i) => {
      const cuenta = cuentas.find((c) => c.id === p.cuenta_id);
      if (!cuenta || p.importe === null) {
        siguientes.push(p);
        return;
      }
      const conVieja = proponerImporte(
        viejo.objetivo,
        pagos.slice(0, i),
        cuentas,
        viejo.monedaObjetivo,
        cuenta,
        viejo.cotizacion,
      );
      if (Math.abs(conVieja - p.importe) >= 0.005) {
        siguientes.push(p);
        return;
      }
      const conNueva = proponerImporte(
        objetivo,
        siguientes,
        cuentas,
        monedaObjetivo,
        cuenta,
        cotizacion,
      );
      if (Math.abs(conNueva - p.importe) < 0.005) {
        siguientes.push(p);
        return;
      }
      cambio = true;
      ida.current.delete(p.id);
      siguientes.push({ ...p, importe: conNueva });
    });
    if (cambio) onChange(siguientes);
    // Solo reacciona a lo que hay que cobrar y a la cotizacion.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objetivo, cotizacion, monedaObjetivo]);

  const agregar = useCallback(() => {
    const p = nuevoPago(cuentas, pagos, objetivo, monedaObjetivo, cotizacion);
    if (p) onChange([...pagos, p]);
  }, [cuentas, pagos, objetivo, monedaObjetivo, cotizacion, onChange]);

  const quitable = pagos.length > 1 || permitirVacio;

  return (
    <div className="space-y-2">
      {pagos.map((p) => (
        <FilaPago
          key={p.id}
          pago={p}
          cuentas={cuentas}
          onChange={(cambios) => actualizar(p.id, cambios)}
          onQuitar={quitable ? () => onChange(pagos.filter((x) => x.id !== p.id)) : undefined}
        />
      ))}

      {!unoSolo || pagos.length === 0 ? (
        <Button variant="dashed" size="sm" onClick={agregar}>
          {pagos.length === 0 ? etiquetaVacio : "Agregar otro medio de pago"}
        </Button>
      ) : null}
    </div>
  );
}
